/** Events accepted by the component hierarchy and mediator. */
export type TypedEvent = {
  readonly type: string
}

export type EventHandling = "handled" | "unhandled"

export interface EventDispatchContext<Event extends TypedEvent> {
  readonly node: EventNode<Event>
  readonly dispatch: (event: Event) => EventDispatchResult<Event>
}

export type EventHandler<Event extends TypedEvent> = (
  event: Event,
  context: EventDispatchContext<Event>,
) => EventHandling

export type EventDispatchResult<Event extends TypedEvent> =
  | {
      readonly status: "handled"
      readonly handledBy: EventNode<Event>
    }
  | {
      readonly status: "unhandled"
      readonly handledBy: null
    }
  | {
      readonly status: "rejected"
      readonly reason: "disposed"
    }

type EventForType<
  Event extends TypedEvent,
  Type extends Event["type"],
> = Extract<Event, { type: Type }>

export interface EventNode<Event extends TypedEvent> {
  readonly parent: EventNode<Event> | null
  readonly isDisposed: boolean
  readonly createChild: () => EventNode<Event>
  readonly subscribe: (handler: EventHandler<Event>) => () => void
  readonly on: <Type extends Event["type"]>(
    type: Type,
    handler: (
      event: EventForType<Event, Type>,
      context: EventDispatchContext<Event>,
    ) => EventHandling,
  ) => () => void
  readonly dispatch: (event: Event) => EventDispatchResult<Event>
  readonly dispose: () => void
}

interface HandlerRegistration<Event extends TypedEvent> {
  readonly handler: EventHandler<Event>
  active: boolean
}

interface NodeState<Event extends TypedEvent> {
  readonly parent: NodeState<Event> | null
  readonly handlers: Set<HandlerRegistration<Event>>
  disposed: boolean
}

function hasEventType<Event extends TypedEvent, Type extends Event["type"]>(
  event: Event,
  type: Type,
): event is EventForType<Event, Type> {
  return event.type === type
}

function disposedDispatchResult<
  Event extends TypedEvent,
>(): EventDispatchResult<Event> {
  return {
    status: "rejected",
    reason: "disposed",
  }
}

function disposeNode<Event extends TypedEvent>(state: NodeState<Event>): void {
  if (state.disposed) return

  state.disposed = true
  for (const registration of state.handlers) {
    registration.active = false
  }
  state.handlers.clear()
}

function isStateDisposed<Event extends TypedEvent>(
  state: NodeState<Event>,
): boolean {
  let current: NodeState<Event> | null = state
  while (current) {
    if (current.disposed) return true
    current = current.parent
  }
  return false
}

function dispatchFrom<Event extends TypedEvent>(
  node: EventNode<Event>,
  state: NodeState<Event>,
  event: Event,
): EventDispatchResult<Event> {
  if (isStateDisposed(state)) return disposedDispatchResult()

  let currentNode: EventNode<Event> | null = node
  let currentState: NodeState<Event> | null = state

  while (currentNode && currentState) {
    if (isStateDisposed(currentState)) return disposedDispatchResult()

    const dispatchNode = currentNode
    const context: EventDispatchContext<Event> = {
      node: dispatchNode,
      dispatch: (nestedEvent: Event): EventDispatchResult<Event> =>
        dispatchNode.dispatch(nestedEvent),
    }
    const registrations = Array.from(currentState.handlers)
    for (const registration of registrations) {
      if (isStateDisposed(currentState)) return disposedDispatchResult()
      if (!registration.active || !currentState.handlers.has(registration)) {
        continue
      }

      const handling = registration.handler(event, context)
      if (handling === "handled") {
        return {
          status: "handled",
          handledBy: currentNode,
        }
      }
      if (isStateDisposed(currentState)) return disposedDispatchResult()
    }

    currentNode = currentNode.parent
    currentState = currentState.parent
  }

  return {
    status: "unhandled",
    handledBy: null,
  }
}

function createNode<Event extends TypedEvent>(
  parent: EventNode<Event> | null,
  parentState: NodeState<Event> | null,
): EventNode<Event> {
  const state: NodeState<Event> = {
    parent: parentState,
    handlers: new Set<HandlerRegistration<Event>>(),
    disposed: false,
  }

  let node: EventNode<Event>
  node = Object.freeze({
    parent,
    get isDisposed(): boolean {
      return isStateDisposed(state)
    },
    createChild: (): EventNode<Event> => {
      if (isStateDisposed(state)) {
        throw new Error("Cannot create a child of a disposed node")
      }

      return createNode(node, state)
    },
    subscribe: (handler: EventHandler<Event>): (() => void) => {
      if (isStateDisposed(state)) {
        throw new Error("Cannot subscribe to a disposed node")
      }

      const registration: HandlerRegistration<Event> = {
        handler,
        active: true,
      }
      state.handlers.add(registration)
      return (): void => {
        if (!registration.active) return
        registration.active = false
        state.handlers.delete(registration)
      }
    },
    on: <Type extends Event["type"]>(
      type: Type,
      handler: (
        event: EventForType<Event, Type>,
        context: EventDispatchContext<Event>,
      ) => EventHandling,
    ): (() => void) =>
      node.subscribe((event, context): EventHandling => {
        if (!hasEventType(event, type)) return "unhandled"
        return handler(event, context)
      }),
    dispatch: (event: Event): EventDispatchResult<Event> =>
      dispatchFrom(node, state, event),
    dispose: (): void => {
      disposeNode(state)
    },
  })

  return node
}

/** Create the root of a component event hierarchy. */
export function createRoot<
  Event extends TypedEvent = TypedEvent,
>(): EventNode<Event> {
  return createNode<Event>(null, null)
}

/** Create a child node while preserving the parent's immutable link. */
export function createChild<Event extends TypedEvent>(
  parent: EventNode<Event>,
): EventNode<Event> {
  return parent.createChild()
}

export type MediatorTransitionResult<State, Effect> =
  | {
      readonly status: "handled"
      readonly state: State
      readonly effects?: readonly Effect[]
    }
  | {
      readonly status: "unhandled"
    }

export type MediatorTransition<
  State,
  Event extends TypedEvent,
  Effect = never,
> = (state: State, event: Event) => MediatorTransitionResult<State, Effect>

export type MediatorDispatchResult<State, Effect> =
  | {
      readonly status: "handled"
      readonly state: State
      readonly effects: readonly Effect[]
    }
  | {
      readonly status: "unhandled"
      readonly state: State
    }
  | {
      readonly status: "rejected"
      readonly reason: "disposed"
      readonly state: State
    }

export interface MediatorOptions<Effect> {
  readonly runEffect?: (effect: Effect) => void
}

export type MediatorSubscriber<State, Event extends TypedEvent> = (
  state: State,
  event: Event,
) => void

export interface Mediator<State, Event extends TypedEvent, Effect = never> {
  readonly isDisposed: boolean
  readonly getSnapshot: () => State
  readonly subscribe: (listener: MediatorSubscriber<State, Event>) => () => void
  readonly dispatch: (event: Event) => MediatorDispatchResult<State, Effect>
  readonly dispose: () => void
}

interface MediatorNotification<State, Event extends TypedEvent, Effect> {
  readonly state: State
  readonly event: Event
  readonly effects: readonly Effect[]
}

/** Create a synchronous state machine whose transition function remains pure. */
export function createMediator<State, Event extends TypedEvent, Effect = never>(
  initialState: State,
  transition: MediatorTransition<State, Event, Effect>,
  options: MediatorOptions<Effect> = {},
): Mediator<State, Event, Effect> {
  let state = initialState
  let disposed = false
  let isProcessing = false
  const listeners = new Set<MediatorSubscriber<State, Event>>()
  const pendingNotifications: Array<
    MediatorNotification<State, Event, Effect>
  > = []

  const processNotifications = (): void => {
    while (pendingNotifications.length > 0) {
      if (disposed) {
        pendingNotifications.length = 0
        return
      }

      const notification = pendingNotifications.shift()
      if (!notification) return

      for (const listener of Array.from(listeners)) {
        if (disposed) return
        if (listeners.has(listener)) {
          listener(notification.state, notification.event)
        }
      }
      if (options.runEffect) {
        for (const effect of notification.effects) {
          if (disposed) return
          options.runEffect(effect)
        }
      }
    }
  }

  const mediator: Mediator<State, Event, Effect> = {
    get isDisposed(): boolean {
      return disposed
    },
    getSnapshot: (): State => state,
    subscribe: (listener: MediatorSubscriber<State, Event>): (() => void) => {
      if (disposed) {
        throw new Error("Cannot subscribe to a disposed mediator")
      }

      listeners.add(listener)
      return (): void => {
        listeners.delete(listener)
      }
    },
    dispatch: (event: Event): MediatorDispatchResult<State, Effect> => {
      if (disposed) {
        return {
          status: "rejected",
          reason: "disposed",
          state,
        }
      }

      const result = transition(state, event)
      if (result.status === "unhandled") {
        return {
          status: "unhandled",
          state,
        }
      }

      const nextState = result.state
      state = nextState
      const effects = result.effects ?? []
      pendingNotifications.push({
        state: nextState,
        event,
        effects,
      })
      if (!isProcessing) {
        isProcessing = true
        try {
          processNotifications()
        } catch (error) {
          pendingNotifications.length = 0
          throw error
        } finally {
          isProcessing = false
        }
      }

      return {
        status: "handled",
        state: nextState,
        effects,
      }
    },
    dispose: (): void => {
      if (disposed) return
      disposed = true
      listeners.clear()
      pendingNotifications.length = 0
    },
  }

  return Object.freeze(mediator)
}
